/**
 * components/journal/JournalPanel.tsx
 * Trade journal — moved out of RightPanel's cramped sidebar into its own
 * top-level "Journal & Analytics" tab (see store's `mainTab`).
 */

import { useEffect } from 'react';
import clsx           from 'clsx';
import { useAccount } from '../../store';
import { tradesApi }  from '../../lib/api';

export function JournalPanel() {
  const { trades, setTrades } = useAccount();

  useEffect(() => {
    tradesApi.list({ limit: 20 }).then(setTrades).catch(console.warn);
  }, [setTrades]);

  // Guard: ensure trades is always an array
  const safeTrades = Array.isArray(trades) ? trades : [];

  return (
    <div className="p-3 overflow-y-auto">
      <div className="section-label mb-3">Trade Journal</div>
      {safeTrades.length === 0 ? (
        <div className="text-2xs text-ink-3 leading-relaxed">
          No trades logged yet. Select a ticker, generate a plan,
          then click "Log Trade" to record it.
        </div>
      ) : (
        <div className="space-y-2">
          {safeTrades.map((t, i) => {
            const pnl    = typeof t.pnl_dollars === 'number' ? t.pnl_dollars : null;
            const isWin  = t.outcome === 'win';
            const isOpen = t.status === 'open';

            return (
              <div key={i} className={clsx(
                'rounded-lg p-3 border-l-2',
                isOpen  ? 'bg-bg-2 border-l-sky'
                : isWin ? 'bg-bull/5 border-l-bull'
                :          'bg-bear/5 border-l-bear'
              )}>
                <div className="flex justify-between items-start">
                  <div className="text-xs font-medium truncate mr-2">
                    {t.sym} {t.contract_description}
                  </div>
                  <div className={clsx('text-xs font-mono flex-shrink-0',
                    isOpen ? 'text-sky' : isWin ? 'text-bull' : 'text-bear')}>
                    {isOpen ? 'OPEN'
                      : pnl !== null
                      ? `${pnl >= 0 ? '+' : ''}$${pnl.toFixed(0)}`
                      : '—'}
                  </div>
                </div>
                <div className="text-2xs text-ink-3 mt-1 leading-snug">
                  {t.entry_date} · Entry ${t.entry_option_price ?? '—'} · {t.contracts ?? 1} contract{(t.contracts ?? 1) !== 1 ? 's' : ''}
                  {t.r_multiple != null ? ` · ${t.r_multiple >= 0 ? '+' : ''}${t.r_multiple.toFixed(1)}R` : ''}
                </div>
                {t.lots && t.lots.some(l => l.status === 'closed') && (
                  <div className="text-2xs text-ink-3 mt-1 leading-snug space-y-0.5">
                    {t.lots.filter(l => l.status === 'closed').map(l => (
                      <div key={l.lot}>
                        Lot {l.lot} · {l.contracts}x · {l.exit_reason} @ ${(l.exit_price ?? 0).toFixed(2)}
                        {' '}({(l.pnl_dollars ?? 0) >= 0 ? '+' : ''}${(l.pnl_dollars ?? 0).toFixed(0)})
                      </div>
                    ))}
                  </div>
                )}
                {t.notes && (
                  <div className="text-2xs text-ink-3 mt-1 italic">{t.notes}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
